import { resolveProjectName } from "@inrev/common";
import type { SuretyAccount } from "@portal/api/account";
import { BondRequest } from "@portal/api/bond-request/agent";
import type { UseFollowersReturn } from "@portal/api/followers";
import { useFetchAgentPreviews } from "@portal/api/user/agent";
import { cn } from "@portal/components/lib/utils";
import { Icon, Text } from "@portal/components/primitives";
import { FollowerControls } from "@portal/components/ui/FollowerControls";
import { getStatusTagByStatus } from "@portal/components/ui/StatusTag";
import { useIsMinimalBondUIEnabled } from "@portal/hooks/useIsMinimalBondUIEnabled";
import { DateTime } from "luxon";
import { ReactNode } from "react";
import { Link } from "react-router-dom";
import { formatDisplayTitle } from "../../../utils";

type BondRequestCardHeaderProps = {
	request: BondRequest;
	account: SuretyAccount;
	actionButtons?: ReactNode;
	followersControl: UseFollowersReturn;
	className?: string;
};

export const BondRequestCardHeader = ({
	request,
	account,
	actionButtons,
	followersControl,
	className,
}: BondRequestCardHeaderProps) => {
	const isMinimalUIEnabled = useIsMinimalBondUIEnabled();

	return (
		<div className={cn("w-full h-fit flex space-x-[50px]", className)}>
			<div className="flex-1 flex flex-col space-y-[30px]">
				<div className="flex flex-col space-y-4">
					<div className="flex flex-col space-y-[16px]">
						{getStatusTagByStatus(request.status)}
						<div className="flex flex-col space-y-[10px]">
							<Text type="heading" color="primary" className="font-semibold leading-[32px]">
								{`${formatDisplayTitle(request.displayTitle)} Request`}
							</Text>
							<div className="flex flex-col space-y-[7px] pl-[2px]">
								<div className="flex items-baseline gap-x-[4px]">
									<Text type="body" color="secondary">
										for
									</Text>
									<Link to={`/accounts/${request.suretyAccountId}`}>
										<Text type="body" color="secondary" className="hover:underline">
											{request.principal.name}
										</Text>
									</Link>
								</div>
								<div className="flex flex-col space-y-[6px]">
									{account.primaryCompanyId !== request.principal.id && (
										<div className="flex items-center space-x-[5px]">
											<Icon type="building-office-alt" size="mini" color="secondary" />
											<Link to={`/accounts/${request.suretyAccountId}`}>
												<Text type="body" color="secondary" className="hover:underline">
													{account.displayName}
												</Text>
											</Link>
										</div>
									)}
									<div className="flex items-center space-x-[7px]">
										<Icon type="calendar" size="mini" color="secondary" />
										<Text type="body" color="secondary">
											Submitted{" "}
											{DateTime.fromISO(request.submittedAt).toLocaleString(DateTime.DATE_MED)}
										</Text>
									</div>
									{!isMinimalUIEnabled && (
										<div className="flex space-x-[15px] !mt-[28px]">
											<div className="h-full w-[2.5px] min-w-[2.5px] rounded-full bg-bc-default"></div>
											<Text type="caption" color="secondary" className="leading-[22px] italic">
												{resolveProjectName(request.project.name, request.project.description)}
											</Text>
										</div>
									)}
								</div>
							</div>
						</div>
					</div>
					<FollowerControls
						{...followersControl}
						useFetchAgentPreviews={useFetchAgentPreviews}
						notFollowingLabel="Follow this request"
						trackingId="bond-request-agent-follow"
						manageFollowersSubtext="Followers will receive email notifications for all comments and changes to the status of this bond request"
					/>
				</div>
			</div>
			{actionButtons && (
				<div className="w-[165px] flex flex-col space-y-[5px]">{actionButtons}</div>
			)}
		</div>
	);
};
